import { FC, useState } from "react";
import { Box, Skeleton, Stack, Tooltip, Typography, css, styled, StackProps } from "@mui/material";
import { IconDownloadBlue, IconSearchBlue } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { fetchSharedSignedUrlApi, useSharedFileSignedUrl } from "@/api/services/main/sharing";
import { background, button, primary, text } from "@/theme/colors";
import { textEllipsis } from "@/theme/minxin";
import { typography } from "@/theme/typography";
import { showError } from "@/utils/error";
import { downloadFileByUrl } from "@/utils/export";
import { SharedFilePreviewModal } from "@/features/sharing/components/SharedFilePreviewModal";
import { TTalentUploadFile } from "@/features/talent/types/talentForm";
import { formatFileNameWithDate } from "@/features/talent/utils/format-file-name-with-date";

type TSharedTalentFileItemProps = {
  filePath: string;
  shareUrl: string;
  files?: TTalentUploadFile[];
  currentFileIndex?: number;
  maxWidth?: number;
  /** Optional title to prepend to filename as: title（filename） */
  title?: string;
  /** When provided, the date is inlined into the displayed filename (e.g. `report20251119.pdf`). */
  updatedAt?: string | null;
} & StackProps;

const PREVIEWABLE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "pdf", "tif", "tiff"];

/** Get file extension from path */
const getExtension = (path: string) => path.split(".").pop()?.toLowerCase() ?? "";

/** Extract display name from filePath, removing timestamp prefix */
const getFileName = (filePath: string) => {
  const segments = filePath.split("/");
  const fullName = segments[segments.length - 1] || filePath;
  const underscoreIdx = fullName.indexOf("_");
  return underscoreIdx > 0 ? fullName.substring(underscoreIdx + 1) : fullName;
};

/** File item with download/preview for shared (no-auth) view — uses shared signed URL API */
export const SharedTalentFileItem: FC<TSharedTalentFileItemProps> = ({ filePath, shareUrl, files, currentFileIndex = 0, maxWidth, title, updatedAt, ...rest }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { isLoading } = useSharedFileSignedUrl(filePath, shareUrl);

  const fileName = getFileName(filePath);
  const displayFileName = title ? `${title}（${fileName}）` : formatFileNameWithDate(fileName, updatedAt);
  const ext = getExtension(filePath);
  const isPreviewable = PREVIEWABLE_EXTENSIONS.includes(ext);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const res = await fetchSharedSignedUrlApi(filePath, shareUrl);
      await downloadFileByUrl(fileName, res.data.signedUrl);
    } catch (error) {
      showError(error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0, maxWidth }} {...rest}>
        <FileNameWrap isDownloading={isDownloading}>
          <Tooltip
            arrow
            slotProps={{
              tooltip: { sx: { backgroundColor: background.white, color: text.primary, boxShadow: "0px 2px 8px 0px #00000040" } },
              arrow: { sx: { color: background.white } },
            }}
            title={
              <Typography variant="cap12" sx={{ backgroundColor: background.white, color: text.primary }}>
                {fileName}
              </Typography>
            }
          >
            <FileName disableEllipsis={!!updatedAt}>{isLoading ? <Skeleton variant="text" height={32} width={200} /> : displayFileName}</FileName>
          </Tooltip>
        </FileNameWrap>
        <Stack direction="row" alignItems="center" gap={1} sx={{ ml: "16px" }}>
          {isPreviewable && (
            <IconBox sx={{ width: 20, height: 20, padding: 0, flexShrink: 0 }} onClick={() => setPreviewOpen(true)}>
              <IconSearchBlue width={20} height={20} color={primary[500]} />
            </IconBox>
          )}
          <IconBox
            sx={{
              width: 20,
              height: 20,
              padding: 0,
              flexShrink: 0,
              cursor: isDownloading ? "not-allowed" : "pointer",
              opacity: isDownloading ? 0.5 : 1,
            }}
            onClick={isDownloading ? undefined : handleDownload}
            disabled={isDownloading}
          >
            <IconDownloadBlue color={isDownloading ? button.disable : primary[500]} width={20} height={20} />
          </IconBox>
        </Stack>
      </Stack>

      {isPreviewable && files && files.length > 0 && (
        <SharedFilePreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          files={files}
          currentFileIndex={currentFileIndex}
          shareUrl={shareUrl}
        />
      )}
    </>
  );
};

const options = { shouldForwardProp: (propName: string) => !["isDownloading"].includes(propName) };

const FileNameWrap = styled(Box, options)<{ isDownloading: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
  ${({ isDownloading }) =>
    isDownloading &&
    css`
      pointer-events: none;
      opacity: 0.8;
    `}
`;

const FileName = styled(Typography, { shouldForwardProp: (prop) => prop !== "disableEllipsis" })<{ disableEllipsis?: boolean }>`
  ${({ disableEllipsis }) => !disableEllipsis && textEllipsis};
  ${typography.body14};
`;
